import React, { useRef, useEffect } from "react";
import {useNavigate, useNavigationType, useParams} from "react-router-dom";
import { motion, useAnimate } from "motion/react";

//blocks
import Goals from "components/focus-blocks/Goals";
import Research from "components/focus-blocks/Research";
import Challenges from "components/focus-blocks/Challenges";
import Scroller from "components/focus-blocks/Scroller";
import Outcomes from "components/focus-blocks/Outcomes";
import Gallery from "components/focus-blocks/Gallery";
import Text from "components/focus-blocks/Text";
import Video from "components/focus-blocks/Video";
import works from "../data/works"

import closeIcon from "img/icons/close.svg";

const Focus = (props) => {

  const params = useParams();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const work = works.filter(work => work.slug === params?.project)[0];
  const [scope, animate] = useAnimate();

  const getTagString = () => {
    if (!work) return;

    let tagString = "";

    work.tags.map((tag, i) => {
      if (i !== work.tags.length - 1) {
        tagString = tagString.concat(`${tag} \u2219 `);
      }
      else {
        tagString = tagString.concat(`${tag}`);
      }
    });

    return tagString;
  }

  const closeFocusAnim = () => {
    animate(".focus-header", {opacity: 0}, {duration: 0.2, ease: 'easeInOut', onComplete: () => navigate(-1)});
  }
  const onBottomClose = () => {
    onTop();
    closeFocusAnim();
  }
  const onTop = () => {
    scope.current.scrollTo({top: 0, behavior: "smooth"});
  }

  const spaceText = text => {
    return text.split('\n').map((str, i) => <p key={i}>{str}</p>);
  }

  const getComponent = key => {

    const comp = {
      goals: Goals,
      research: Research,
      challenges: Challenges,
      scroller: Scroller,
      outcomes: Outcomes,
      gallery: Gallery,
      text: Text,
      video: Video
    }

    return comp[key];
  }

  useEffect(() => {
    if (work === undefined) navigate('/');
  }, []);

  const isPOP = navigationType === "POP";
  const DELAY = isPOP ? 0 : 0.5;

  const item = {
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeInOut", delay: DELAY} },
    hidden: { opacity: 0, y: 30, transition: { duration: 0.8, ease: "easeInOut", delay: DELAY } }
  }

  if (!work) return null;
  return (
    <motion.div
      ref={scope}
      // initial={{backgroundColor: `rgba(255,255,255,${navigationType === "POP" ? 1 : 0})`}}
      // animate={{backgroundColor: 'rgba(255,255,255,1)', transition: {delay: 0.1, duration: 0.6, ease: 'circIn'}}}
      className="focus invert"
    >
      <motion.div
        initial={"hidden"}
        animate={'visible'}
        className="focus-hero"
      >
        <motion.div
          className="hero-image"
          style={{ backgroundImage: `url(img/projects/${work.slug}/thumb.jpg)` }}
          alt="project"
          initial={{ opacity: isPOP ? 1 : 0, transition: { duration: 0.8, ease: "easeInOut", delay: DELAY*2} }}
          animate={{ opacity: 1, transition: { duration: 0.8, ease: "easeInOut", delay: DELAY*2} }}
        />

        <motion.div
          initial={{ opacity: isPOP ? 1 : 0, transition: { duration: 0.8, ease: "easeInOut", delay: DELAY} }}
          animate={{ opacity: 1, transition: { duration: 0.8, ease: "easeInOut", delay: DELAY} }}
          className="tile focus-header invert"
        >
          <motion.h1 variants={item}>{work.title}</motion.h1>
          <motion.button variants={item} className="icon close" type="button" onClick={closeFocusAnim}>
            <img src={closeIcon} alt="close project" />
          </motion.button>
          <motion.div variants={item} className="focus-info">

            <ul variants={item} className="info-list">
              <li>
                <h4><span>For</span></h4><h4>{work.info.for}</h4>
              </li>
              <li>
                <h4><span>Outcome</span></h4><h4>{work.info.outcome}</h4>
              </li>
              <li>
                <h4><span>When</span></h4><h4>{work.year}</h4>
              </li>
              <li>
                <h4><span>Role</span></h4><h4>{work.info.role}</h4>
              </li>
              <li>
                <h4><span>Tags</span></h4><h4>{getTagString()}</h4>
              </li>
            </ul>

            <div className="project-image-sub2" style={{ backgroundImage: `url(img/projects/${work.slug}/thumb3.jpg)` }}></div>

            <article>
              <h3>Overview</h3>
              {spaceText(work.focus.desc)}
              {work.focus.link &&
                <div className="link-out">
                  <div className="title">
                    <h3>{work.focus.link[0].type}</h3>
                    {(work.focus.link[0].alert !== null) && 
                      <h4>({work.focus.link[0].alert})</h4>
                    }
                  </div>
                  {work.focus.link.map(l => 
                    <a key={l.url} href={l.url} target="_blank" rel="noopener">{l.title}</a>
                  )}
                </div>
              }
            </article>
          </motion.div>
        </motion.div>
      </motion.div>
      <motion.div
        className="keep-scrolling"
        initial={{ opacity: 0, transition: { duration: 0.8, ease: "easeInOut", delay: DELAY} }}
        animate={{ opacity: 1, transition: { duration: 0.8, ease: "easeInOut", delay: DELAY} }}
      >
        <div className="rotate-text">
          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="300px" height="300px" viewBox="0 0 300 300" enableBackground="new 0 0 300 300" xmlSpace="preserve">
            <defs>
              <path id="circlePath" d=" M 150, 150 m -60, 0 a 60,60 0 0,1 120,0 a 60,60 0 0,1 -120,0 " />
            </defs>
            <circle cx="150" cy="100" r="75" fill="none" />
            <circle cx="150" cy="150" r="40" fill="#f0a76c" />
            <g>
              <use xlinkHref="#circlePath" fill="none" />
              <text fill="#fff">
                <textPath xlinkHref="#circlePath">&nbsp;&nbsp;&nbsp;&nbsp;Keep Scrolling&nbsp;&nbsp;&nbsp;&nbsp;&#8277;&nbsp;&nbsp;&#8277;&nbsp;&nbsp;&#8277;&nbsp;&nbsp;&nbsp;&nbsp;Keep Scrolling&nbsp;&nbsp;&nbsp;&nbsp;&#8277;&nbsp;&nbsp;&#8277;&nbsp;&nbsp;&#8277;&nbsp;&nbsp;</textPath>
              </text>
            </g>
          </svg>
        </div>
        {/* <img src={arrow} alt="down arrow"/> */}
      </motion.div>
      <div className="inner-focus">
        <div className="focus-body">
          {work.focus.components.map((block, i) => 
            React.createElement(getComponent(block), {work, spaceText, key: i, i})
          )}
          <section className="focus-footer tile">
            <button onClick={onTop}>
              <div>
                <p>Back to Top</p>
              </div>
            </button>
            <button onClick={onBottomClose}>
              <div>
                <p>Close</p>
              </div>
            </button>
          </section>
        </div>
      </div>
    </motion.div>
  )
}

export default Focus;